// Browser-compatible version of excelDesigner.js
// Professional financial model styling for Excel export
// Inspired by Goldman Sachs / PE firm / investment bank model conventions

import ExcelJS from 'exceljs';

/** Convert #RRGGBB or RRGGBB hex to FFRRGGBB ARGB for ExcelJS */
function hexToArgb(hex) {
  if (!hex) return null;
  const clean = hex.replace('#', '').toUpperCase();
  if (clean.length === 6) return `FF${clean}`;
  if (clean.length === 8) return clean;
  return null;
}

/** Blend an ARGB color toward white at the given alpha (0–1). */
function lightTint(argb, alpha = 0.15) {
  if (!argb || argb.length < 8) return 'FFFFFFFF';
  const r = parseInt(argb.slice(2, 4), 16);
  const g = parseInt(argb.slice(4, 6), 16);
  const b = parseInt(argb.slice(6, 8), 16);
  const blend = (c) => Math.round(255 * (1 - alpha) + c * alpha);
  const toHex = (v) => blend(v).toString(16).padStart(2, '0').toUpperCase();
  return `FF${toHex(r)}${toHex(g)}${toHex(b)}`;
}
// ─── Color Palette (ARGB) ────────────────────────────────────────────────────
// Standard PE / IB model color conventions
const COLORS = {
  // ── Section header bands
  NAVY:               'FF1F3864',  // Deep navy — section banners, title bar
  NAVY_MID:           'FF2F5496',  // Medium navy — accent elements

  // ── Data row fills
  ACTUAL_FILL:        'FFF4F4F4',  // Very light gray for actual-period data cells
  ACTUAL_HDR_FILL:    'FFE8E8E8',  // Slightly darker for "Actual" column header
  FORECAST_FILL:      'FFFFFFFF',  // White for forecast-period data cells
  FORECAST_HDR_FILL:  'FFE9F0FA',  // Light periwinkle for "Forecast" column header

  // ── Total / Subtotal rows
  TOTAL_FILL:         'FFD9E1F2',  // Classic light periwinkle — group total rows
  SUBTOTAL_FILL:      'FFEEF2FA',  // Lighter periwinkle — display-group subtotals
  DG_HEADER_FILL:     'FFF5F8FF',  // Very light blue — display-group category headers

  // ── Key cash rows (Beginning / Ending cash)
  CASH_FILL:          'FFFCE4D6',  // Warm peach tint — cash balance lines

  // ── Driver rows
  DRIVER_FILL:        'FFF9F9F9',  // Near-white — driver data cells
  DRIVER_HDR_FILL:    'FFF0F0F0',  // Light gray — "Drivers" sub-header

  // ── Borders
  BLACK:              'FF000000',
  BORDER_GRAY:        'FFD0D7E2',  // Light gray for subtle separators
  BORDER_NAVY:        'FF1F3864',  // Navy for header underlines

  // ── Text
  WHITE:              'FFFFFFFF',
  TEXT_DARK:          'FF1A1A1A',  // Near-black for data text
  TEXT_NAVY:          'FF1F3864',  // Navy for section labels, totals
  TEXT_MUTED:         'FF737373',  // Muted gray for drivers, subtitles
  TEXT_ACTUAL:        'FF404040',  // Dark gray for "Actual" labels
  TEXT_FORECAST:      'FF2F5496',  // Blue for "Forecast" labels
  BLUE_INPUT:         'FF0070C0',  // Professional blue for hard-coded values
};

// ─── Typography ──────────────────────────────────────────────────────────────
const FONT        = { name: 'Calibri', size: 10 };
const FONT_BOLD   = { name: 'Calibri', size: 10, bold: true };
const FONT_ITALIC = { name: 'Calibri', size: 10, italic: true };
const FONT_BOLD_ITALIC = { name: 'Calibri', size: 10, bold: true, italic: true };
const FONT_SM     = { name: 'Calibri', size: 10 };
const FONT_SM_ITALIC = { name: 'Calibri', size: 10, italic: true };

// ─── Number Formats — PE / Investment Bank convention ────────────────────────
// • $ sign adjacent to number (not floating to left edge)
// • 0 decimal places for currency
// • Red + parentheses for negatives
// • _) trailing space aligns positives with parenthesised negatives
const NUM_FORMATS = {
  currency: '$#,##0_);[Red]($#,##0)',
  percent:  '0.0%',
  count:    '#,##0',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Build provenance map: (rowId, date) -> source type
 * Returns: Map with keys like "rowId:date" -> "manual" | "default" | "formula" | "cache" | "actual"
 */
function buildProvenanceMap(data, modelIndex, dateCol) {
  const provenance = new Map();
  const actualDates = new Set(data.calendar?.periods?.actual ?? []);
  const forecastDates = new Set(data.calendar?.periods?.forecast ?? []);

  function walkNodes(nodes) {
    for (const node of nodes || []) {
      if (node.kind === 'row' || node.kind === 'group') {
        for (const [dateStr] of Object.entries(dateCol)) {
          const isActual = actualDates.has(dateStr);
          const segment = isActual ? node.actuals : node.forecast;

          if (!segment) {
            provenance.set(`${node.id}:${dateStr}`, 'cache');
          } else if (segment.method === 'formula') {
            provenance.set(`${node.id}:${dateStr}`, 'formula');
          } else if (segment.method === 'values') {
            const manual = (segment.values || []).find(v => v.type === 'manual');
            const manualVal = manual?.values?.[dateStr];
            if (manualVal !== undefined) {
              provenance.set(`${node.id}:${dateStr}`, 'manual');
            } else if (forecastDates.has(dateStr)) {
              provenance.set(`${node.id}:${dateStr}`, 'default');
            } else {
              provenance.set(`${node.id}:${dateStr}`, 'cache');
            }
          } else if (segment.method === 'sql') {
            provenance.set(`${node.id}:${dateStr}`, 'actual');
          } else {
            provenance.set(`${node.id}:${dateStr}`, 'cache');
          }
        }
      }
      if (node.children) walkNodes(node.children);
    }
  }

  for (const table of data.model || []) {
    walkNodes(table.children || []);
  }

  return provenance;
}

/**
 * Check if a row is inside a drivers group
 */
function isDriverRow(node, data) {
  if (!node) return false;
  if (node.id && node.id.includes('drivers')) return true;

  for (const table of data.model || []) {
    function findParent(nodes, targetId, currentParent) {
      for (const n of nodes || []) {
        if (n.id === targetId) return currentParent;
        if (n.children) {
          const found = findParent(n.children, targetId, n);
          if (found !== null) return found;
        }
      }
      return null;
    }
    const parent = findParent(table.children || [], node.id, null);
    if (parent && parent.id && parent.id.includes('drivers')) return true;
  }
  return false;
}

// ─── Main style entry point ───────────────────────────────────────────────────

/**
 * Style the workbook — professional PE / IB financial model design
 */
export function styleWorkbook(
  wb, ws, data, modelIndex, dateCol, labelRow, rowById,
  tableRanges, synTotalRows, driverHeaderRows,
  displayGroupHeaderRows, displayGroupSubtotalRows,
  branding = {}
) {
  // Override navy palette with org brand colors when set
  const brandPrimary   = hexToArgb(branding.primaryColor);
  const brandSecondary = hexToArgb(branding.secondaryColor);
  const primaryLight   = brandPrimary ? lightTint(brandPrimary, 0.15) : null;
  const C = {
    ...COLORS,
    // Section banners + tab color — full primary
    NAVY:           brandPrimary   || COLORS.NAVY,
    NAVY_MID:       brandSecondary || COLORS.NAVY_MID,
    // Date header underline — keep grey, no primary
    BORDER_NAVY:    COLORS.BORDER_GRAY,
    // All text stays grey/dark — no primary text outside titles
    TEXT_NAVY:      COLORS.TEXT_DARK,
    TEXT_FORECAST:  COLORS.TEXT_MUTED,
    // Forecast header band (row 4) — light primary tint
    FORECAST_HDR_FILL: primaryLight || COLORS.FORECAST_HDR_FILL,
    // Forecast label text — same grey as Actual
    TEXT_FORECAST:  'FF404040',
    // Group total rows — light primary tint
    TOTAL_FILL:     primaryLight || COLORS.TOTAL_FILL,
    // Display-group category headers — keep their own subtle tint (unchanged)
  };
  const PERIOD_TYPE_ROW = 4;   // "Actual" / "Forecast" label row
  const HEADER_ROW      = 5;   // Date header row
  const LABEL_COL       = 2;
  const FIRST_DATE_COL  = 3;

  const _driverHeaderRows        = driverHeaderRows        || new Set();
  const _displayGroupHeaderRows  = displayGroupHeaderRows  || new Set();
  const _displayGroupSubtotalRows = displayGroupSubtotalRows || new Set();

  // Build actual/forecast column sets
  const actualDateSet   = new Set(data.calendar?.periods?.actual   ?? []);
  const forecastDateSet = new Set(data.calendar?.periods?.forecast ?? []);
  const actualColSet    = new Set();
  const forecastColSet  = new Set();
  for (const [dateStr, col] of Object.entries(dateCol)) {
    if (actualDateSet.has(dateStr))   actualColSet.add(Number(col));
    if (forecastDateSet.has(dateStr)) forecastColSet.add(Number(col));
  }

  const provenance = buildProvenanceMap(data, modelIndex, dateCol);
  const lastCol = Math.max(...Object.values(dateCol).map(Number));

  // ── Column widths ──────────────────────────────────────────────────────────
  ws.getColumn(1).width = 2;     // Narrow left margin
  ws.getColumn(2).width = 35;    // Label column — wide for indented rows
  for (let col = FIRST_DATE_COL; col <= lastCol; col++) {
    ws.getColumn(col).width = 10;
  }

  // ── Freeze panes / view options ────────────────────────────────────────────
  ws.views = [{
    state:        'frozen',
    xSplit:       2,
    ySplit:       6,             // Freeze below the date header + spacer row
    topLeftCell:  'C7',
    activeCell:   'C7',
    showGridLines: false,
  }];

  // ── Title block styling (rows 1-2) — clean white, no fill ────────────────
  // Row 1: Title — bold dark text on white
  const titleRow = ws.getRow(1);
  titleRow.height = 26;
  for (let col = 1; col <= lastCol; col++) {
    const cell = titleRow.getCell(col);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
  }
  const titleCell = titleRow.getCell(LABEL_COL);
  titleCell.font      = { name: 'Calibri', size: 16, bold: true, color: { argb: C.NAVY } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

  // Row 2: Subtitle — italic muted text on white
  const subtitleRow = ws.getRow(2);
  subtitleRow.height = 18;
  for (let col = 1; col <= lastCol; col++) {
    const cell = subtitleRow.getCell(col);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
  }
  const subtitleCell = subtitleRow.getCell(LABEL_COL);
  subtitleCell.font      = { name: 'Calibri', size: 10, italic: true, color: { argb: C.TEXT_MUTED } };
  subtitleCell.alignment = { vertical: 'middle', horizontal: 'left' };

  // Row 3: Spacer — white
  const spacerRow3 = ws.getRow(3);
  spacerRow3.height = 6;
  for (let col = 1; col <= lastCol; col++) {
    spacerRow3.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
  }

  // ── Period type row (row 4) — Actual / Forecast labels ────────────────────
  const periodTypeRow = ws.getRow(PERIOD_TYPE_ROW);
  periodTypeRow.height = 16;
  periodTypeRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
  periodTypeRow.getCell(LABEL_COL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };

  for (let col = FIRST_DATE_COL; col <= lastCol; col++) {
    const cell = periodTypeRow.getCell(col);
    if (actualColSet.has(col)) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.ACTUAL_HDR_FILL } };
      cell.font = { name: 'Calibri', size: 10, bold: true, italic: true, color: { argb: C.TEXT_ACTUAL } };
    } else {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.FORECAST_HDR_FILL } };
      cell.font = { name: 'Calibri', size: 10, bold: true, italic: true, color: { argb: C.TEXT_FORECAST } };
    }
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  }

  // ── Date header row (row 5) ────────────────────────────────────────────────
  const dateHeaderRow = ws.getRow(HEADER_ROW);
  dateHeaderRow.height = 20;
  dateHeaderRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
  dateHeaderRow.getCell(LABEL_COL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
  dateHeaderRow.getCell(LABEL_COL).border = {
    bottom: { style: 'medium', color: { argb: C.BORDER_GRAY } }
  };

  for (let col = FIRST_DATE_COL; col <= lastCol; col++) {
    const cell = dateHeaderRow.getCell(col);
    // All date header cells are white — no actual/forecast differentiation here
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
    cell.font      = { name: 'Calibri', size: 10, bold: true, color: { argb: C.TEXT_MUTED } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border    = { bottom: { style: 'medium', color: { argb: C.BORDER_GRAY } } };
    cell.numFmt    = 'mmm d';
  }

  // ── Build lookup: rowNumber -> node ───────────────────────────────────────
  const rowToNode = new Map();
  function buildRowToNode(nodes) {
    for (const node of nodes || []) {
      if (node.kind === 'row' || node.kind === 'group') {
        const rowNum = rowById[node.id];
        if (rowNum) rowToNode.set(rowNum, node);
      }
      if (node.children) buildRowToNode(node.children);
    }
  }
  for (const table of data.model || []) {
    buildRowToNode(table.children || []);
  }

  // Track banner rows to skip in main loop
  const bannerRows = new Set();

  // ── Section banner rows ────────────────────────────────────────────────────
  for (const tableRange of tableRanges || []) {
    const { startRow } = tableRange;
    bannerRows.add(startRow);

    const bannerRow = ws.getRow(startRow);
    bannerRow.height = 20;
    bannerRow.font = { name: 'Calibri', size: 10, bold: true, color: { argb: C.WHITE } };
    bannerRow.alignment = { vertical: 'middle' };

    for (let col = 1; col <= lastCol; col++) {
      const cell = bannerRow.getCell(col);
      if (col === 1) {
        // Left margin stays white — gives a clean edge
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
      } else {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.NAVY } };
        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: C.WHITE } };
        if (col === LABEL_COL) {
          cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 0 };
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        }
      }
    }
  }

  // ── Main row-by-row styling loop ───────────────────────────────────────────
  for (const [label, rowNumber] of Object.entries(labelRow)) {
    const rowNum = Number(rowNumber);
    if (rowNum <= HEADER_ROW) continue;
    if (bannerRows.has(rowNum)) continue;
    // Display-group rows handled in dedicated passes below
    if (_displayGroupHeaderRows.has(rowNum) || _displayGroupSubtotalRows.has(rowNum)) continue;

    const rowObj  = ws.getRow(rowNum);
    const node    = rowToNode.get(rowNum);
    const isTotal = synTotalRows.has(rowNum);
    const isDriverHeader = _driverHeaderRows.has(rowNum);
    const isEmphasis = node && (node.id === 'beginning_cash' || node.id === 'ending_cash');
    const isDriver   = node && isDriverRow(node, data);

    // ── Group total rows ─────────────────────────────────────────────────────
    if (isTotal) {
      rowObj.height = 18;
      rowObj.alignment = { vertical: 'middle' };

      for (let col = 1; col <= lastCol; col++) {
        const cell = rowObj.getCell(col);
        if (col === 1) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        } else {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.TOTAL_FILL } };
          cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: C.TEXT_NAVY } };
          cell.border = { top: { style: 'thin', color: { argb: C.BLACK } } };
          if (col === LABEL_COL) {
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'right' };
            cell.numFmt = NUM_FORMATS.currency;
          }
        }
      }
      continue;
    }

    // ── Key cash rows: Beginning Cash / Ending Cash ──────────────────────────
    if (isEmphasis && node) {
      rowObj.height = 20;
      rowObj.alignment = { vertical: 'middle' };

      for (let col = 1; col <= lastCol; col++) {
        const cell = rowObj.getCell(col);
        if (col === 1) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        } else {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.CASH_FILL } };
          cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: C.TEXT_NAVY } };
          // Double underline accounting convention for key balance rows
          cell.border = {
            top:    { style: 'thin',   color: { argb: C.BLACK } },
            bottom: { style: 'double', color: { argb: C.BLACK } },
          };
          if (col === LABEL_COL) {
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'right' };
            if (node.type === 'currency') cell.numFmt = NUM_FORMATS.currency;
            else if (node.type === 'percent') cell.numFmt = NUM_FORMATS.percent;
            else cell.numFmt = NUM_FORMATS.currency;
          }
        }
      }
      continue;
    }

    // ── "Drivers" sub-header rows — handled in dedicated pass ────────────────
    if (isDriverHeader) continue;

    // ── Driver data rows ─────────────────────────────────────────────────────
    if (isDriver && node) {
      rowObj.height = 17;
      rowObj.alignment = { vertical: 'middle' };

      for (let col = 1; col <= lastCol; col++) {
        const cell = rowObj.getCell(col);
        if (col === 1) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        } else if (col === LABEL_COL) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.DRIVER_FILL } };
          cell.font = { ...FONT_SM_ITALIC, color: { argb: C.TEXT_MUTED } };
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        } else {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.DRIVER_FILL } };
          cell.font = { ...FONT_SM_ITALIC, color: { argb: C.TEXT_MUTED } };
          cell.alignment = { vertical: 'middle', horizontal: 'right' };
          if (node.type === 'currency') cell.numFmt = NUM_FORMATS.currency;
          else if (node.type === 'percent') cell.numFmt = NUM_FORMATS.percent;
          else if (node.type === 'count') cell.numFmt = NUM_FORMATS.count;
        }
      }
      continue;
    }

    // ── Regular data rows ────────────────────────────────────────────────────
    rowObj.height = 17;
    rowObj.alignment = { vertical: 'middle' };

    for (let col = 1; col <= lastCol; col++) {
      const cell = rowObj.getCell(col);
      if (col === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        continue;
      }
      if (col === LABEL_COL) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.FORECAST_FILL } };
        cell.font = { ...FONT, color: { argb: C.TEXT_DARK } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        continue;
      }

      // Data cells: all white (no actual column tinting)
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.FORECAST_FILL } };
      cell.alignment = { vertical: 'middle', horizontal: 'right' };
      // Always set font explicitly — without this, cells with no node mapping
      // keep Excel's workbook default (Calibri 11)
      cell.font = { ...FONT, color: { argb: C.TEXT_DARK } };

      // Number format
      if (node) {
        if (node.type === 'currency') cell.numFmt = NUM_FORMATS.currency;
        else if (node.type === 'percent') cell.numFmt = NUM_FORMATS.percent;
        else if (node.type === 'count') cell.numFmt = NUM_FORMATS.count;
      }

      // Provenance override: hard-coded (manual) inputs get blue font
      if (node) {
        const dateStr = Object.keys(dateCol).find(d => dateCol[d] == col);
        if (dateStr && provenance.get(`${node.id}:${dateStr}`) === 'manual') {
          cell.font = { name: 'Calibri', size: 10, color: { argb: C.BLUE_INPUT } };
        }
      }
    }
  }

  // ── Dedicated pass: ALL synthetic total rows ────────────────────────────────
  // (labelRow can miss duplicates; iterate the Set to catch every instance)
  for (const rowNum of synTotalRows) {
    if (bannerRows.has(rowNum)) continue;
    const rowObj = ws.getRow(rowNum);
    rowObj.height = 18;
    rowObj.alignment = { vertical: 'middle' };

    for (let col = 1; col <= lastCol; col++) {
      const cell = rowObj.getCell(col);
      if (col === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        continue;
      }
      cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.TOTAL_FILL } };
      cell.font  = { name: 'Calibri', size: 10, bold: true, color: { argb: C.TEXT_NAVY } };
      cell.border = { top: { style: 'thin', color: { argb: C.BLACK } } };
      if (col === LABEL_COL) {
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      } else {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        cell.numFmt = NUM_FORMATS.currency;
      }
    }
  }

  // ── Dedicated pass: ALL "Drivers" sub-header rows ──────────────────────────
  for (const rowNum of _driverHeaderRows) {
    const rowObj = ws.getRow(rowNum);
    rowObj.height = 16;
    rowObj.alignment = { vertical: 'middle' };

    for (let col = 1; col <= lastCol; col++) {
      const cell = rowObj.getCell(col);
      if (col === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        continue;
      }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.DRIVER_HDR_FILL } };
      if (col === LABEL_COL) {
        cell.font      = { ...FONT_BOLD_ITALIC, color: { argb: C.TEXT_MUTED } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      }
    }
  }

  // ── Dedicated pass: Display-group category header rows ─────────────────────
  // e.g. "Payroll", "Benefits" — bold label, white fill, indent signals hierarchy
  for (const rowNum of _displayGroupHeaderRows) {
    const rowObj = ws.getRow(rowNum);
    rowObj.height = 18;
    rowObj.alignment = { vertical: 'middle' };

    for (let col = 1; col <= lastCol; col++) {
      const cell = rowObj.getCell(col);
      if (col === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        continue;
      }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.DG_HEADER_FILL } };
      if (col === LABEL_COL) {
        cell.font      = { name: 'Calibri', size: 10, bold: true, color: { argb: C.TEXT_NAVY } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      } else {
        // Leave data cells in this row clear — they typically have no values
        cell.font      = { ...FONT };
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    }
  }

  // ── Dedicated pass: Summary ref-child rows (_ref_ keys in rowById) ──────────
  // These rows mirror data-section totals inside the summary section.
  // If the summary section appears before the data sections in the sheet,
  // their trimmed labels get overwritten in labelRow by the data totals,
  // so they're never reached by the main loop — defaulting to Excel's
  // Calibri 11.  This pass catches every _ref_ row and forces Calibri 10.
  for (const [key, rowNumber] of Object.entries(rowById)) {
    if (!key.startsWith('_ref_')) continue;
    const rowNum = Number(rowNumber);
    if (rowNum <= HEADER_ROW) continue;
    if (bannerRows.has(rowNum) || synTotalRows.has(rowNum)) continue;

    const rowObj = ws.getRow(rowNum);
    rowObj.height = 17;
    rowObj.alignment = { vertical: 'middle' };

    for (let col = 1; col <= lastCol; col++) {
      const cell = rowObj.getCell(col);
      if (col === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        continue;
      }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.FORECAST_FILL } };
      if (col === LABEL_COL) {
        cell.font      = { ...FONT, color: { argb: C.TEXT_DARK } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      } else {
        cell.font      = { ...FONT, color: { argb: C.TEXT_DARK } };
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        cell.numFmt    = NUM_FORMATS.currency;
      }
    }
  }

  // ── Dedicated pass: Display-group subtotal rows ────────────────────────────
  for (const rowNum of _displayGroupSubtotalRows) {
    const rowObj = ws.getRow(rowNum);
    rowObj.height = 17;
    rowObj.alignment = { vertical: 'middle' };

    for (let col = 1; col <= lastCol; col++) {
      const cell = rowObj.getCell(col);
      if (col === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.WHITE } };
        continue;
      }
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.SUBTOTAL_FILL } };
      cell.border = { top: { style: 'thin', color: { argb: C.BORDER_GRAY } } };
      if (col === LABEL_COL) {
        cell.font      = { name: 'Calibri', size: 10, bold: true, italic: true, color: { argb: C.TEXT_MUTED } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      } else {
        cell.font      = { name: 'Calibri', size: 10, bold: true, color: { argb: C.TEXT_MUTED } };
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        cell.numFmt    = NUM_FORMATS.currency;
      }
    }
  }

  const colToLetter = (col) => {
    let result = '';
    let current = col;
    while (current > 0) {
      current--;
      result = String.fromCharCode(65 + (current % 26)) + result;
      current = Math.floor(current / 26);
    }
    return result;
  };

  ws.pageSetup = {
    ...(ws.pageSetup || {}),
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: {
      left: 0.2,
      right: 0.2,
      top: 0.2,
      bottom: 0.2,
      header: 0,
      footer: 0,
    },
    printArea: `A1:${colToLetter(lastCol)}${ws.rowCount}`,
  };
}
